// Device wallet client — lives in localStorage but is now anchored to a
// customer phone number. Phone is REQUIRED on creation so we can verify
// ownership at the counter (device short-code + phone = two factors).
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LS_KEY = 'ashop_wallet_v1';

const read = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const write = (data) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(data));
  } catch {}
};

export const clearLocalWallet = () => {
  try { localStorage.removeItem(LS_KEY); } catch {}
};

/** Read the cached local wallet (or null). Never hits the network. */
export const getLocalWallet = () => read();

/**
 * Load the wallet from the server for the currently-cached local id.
 * Returns null if there's no cached id, or throws with `notFound: true`
 * when the server no longer has this wallet (so UI can re-onboard).
 */
export const fetchWallet = async (id) => {
  try {
    const { data } = await axios.get(`${API}/wallet/${id}`);
    return data?.wallet;
  } catch (err) {
    if (err.response?.status === 404) {
      const e = new Error('Wallet not found on server');
      e.notFound = true;
      throw e;
    }
    throw err;
  }
};

/**
 * Create a wallet — phone is required. If the phone is already linked to a
 * wallet, the server returns that same wallet (so multi-device works).
 */
export const createWalletWithPhone = async (phone, name) => {
  const clean = (phone || '').trim();
  if (!clean) throw new Error('Phone number is required');
  const { data } = await axios.post(`${API}/wallet/create`, {
    phone: clean,
    name: (name || '').trim() || undefined,
  });
  const w = data?.wallet;
  if (!w?.id) throw new Error('Server did not return a wallet');
  write({ id: w.id, short_code: w.short_code, phone: w.phone, name: w.name, created_at: w.created_at });
  return { wallet: w, existed: !!data.existed };
};

/** Fetch the public rewards menu (active items only). */
export const fetchPublicRewards = async () => {
  const { data } = await axios.get(`${API}/wallet/rewards/menu`);
  return data?.rewards || [];
};
